// React
import { useEffect } from "react"

// Components
import { BaseTable, Card, DateInput, HeaderMenu, Label, Loading, PermissionLink, Select, TableHeader } from "components"

// Icons
import { IoSendSharp } from "react-icons/io5"

// Third-Party Libraries
import moment from "moment"

// Types
import type { FilterType } from "./types"

// Utils
import { convertNumber } from "utils"
import { default_option } from "pages/Purchase/utils"
import { useListPO } from "pages/Purchase/Register/components/TabAllBills/hooks"
import { useList } from "./utils"
import { PERMISION } from "security"

export default function EditBill(): JSX.Element {
  // Hooks
  const { data, filter, isLoading, setFilterValue } = useList()

  return (
    <section className="container my-3 flex flex-col gap-3">
      <HeaderMenu title="EDIT BILL" />

      <Card>
        <Card.Body>
          <section className="flex flex-wrap gap-3">
            <section className="flex items-center">
              <Label text="START DATE" />

              <DateInput
                className="!w-[150px]"
                selected={moment(filter.date_from).toDate()}
                onChange={(date) => setFilterValue("date_from", moment(date).format("YYYY-MM-DD"))}
              />
            </section>

            <section className="flex items-center">
              <Label text="END DATE" />

              <DateInput
                className="!w-[150px]"
                selected={moment(filter.date_end).toDate()}
                onChange={(date) => setFilterValue("date_end", moment(date).format("YYYY-MM-DD"))}
              />
            </section>

            <section className="flex items-center">
              <Label text="PO" />

              <PoSection
                filter={filter}
                value={filter.purchase_order_id}
                onChange={value => setFilterValue("purchase_order_id", value)}
              />
            </section>
          </section>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="overflow-x-auto max-h-[calc(100vh-450px)]">
            <BaseTable>
              <TableHeader>
                <tr>
                  <th>NO</th>
                  <th>BILL ID#</th>
                  <th>DATE</th>
                  <th>PO</th>
                  <th>STATUS APPROVAL</th>
                  <th>LOCATION</th>
                  <th>QTY DELIVERED</th>
                  <th>TOTAL BILL</th>
                  <th>ACTION</th>
                </tr>
              </TableHeader>

              <tbody>
                {isLoading || !data.length ? (
                  <tr>
                    <td colSpan={9}>
                      <Loading
                        loading={isLoading}
                        errorText="No data available"
                      />
                    </td>
                  </tr>
                ) : data.map((item, key) => (
                  <tr key={key}>
                    <td className="text-center">{key + 1}</td>
                    <td className="text-center">{item.bill_code}</td>
                    <td className="text-center">{moment(item.transaction_date).format("DD/MM/YYYY")}</td>
                    <td className="text-center">{item.po_code}</td>
                    <td>{default_option.find(i => i.value === item.approve_status.toString())?.label}</td>
                    <td>{item.location_name}</td>
                    <td className="text-right">{convertNumber(item.qty_delivered).intoCurrency}</td>
                    <td className="text-right">{convertNumber(item.total_bill).intoCurrency}</td>

                    <td>
                      <PermissionLink permission={PERMISION.PURCHASES.TRANSACTION.EDIT_BILL.UPDATE} to={`/purchase/transaction/edit-bill/${item.bill_id}`}>
                        <IoSendSharp />
                      </PermissionLink>
                    </td>
                  </tr>
                ))}
              </tbody>
            </BaseTable>
          </div>
        </Card.Body>
      </Card>
    </section>
  )
}

function PoSection(params: {
  filter: FilterType
  value?: string
  onChange: (value: string) => void
}): JSX.Element {
  // Hooks
  const { dataPO, isLoadingPO, refetchPO } = useListPO()
  
  // Vars
  const filter = params.filter
  const data = [
    {
      value: "",
      label: "All"
    },
    ...dataPO
  ]

  useEffect(() => {
    refetchPO({
      DateEnd: filter.date_end,
      DateFrom: filter.date_from
    })

    // eslint-disable-next-line
  }, [filter.date_end, filter.date_from])

  return (
    <Select
      className="w-[150px]"
      placeholder="Select PO"
      isLoading={isLoadingPO}
      options={data}
      value={data.find(item => item.value === params.value)}
      onChange={res => {
        if (res) {
          params.onChange(res.value)}
        }
      }
    />
  )
}